import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  downloadCatastoDocument,
  errorMessage,
  fetchCatastoHistory,
  fetchCatastoRequest,
  orderCatastoVisura,
  searchCatastoAddress,
  searchCatastoImmobili,
  type CatastoHistoryRequest,
  type CatastoStepResult,
} from './api';
import { saveDownloadedFile } from './downloadFile';

type PaidStep = 1 | 2 | 3;
type JsonRow = Record<string, unknown>;

interface CatastoChoice {
  id: string;
  label: string;
  detail?: string;
}

const money = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
const dateTime = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const STEP_PRICE: Record<PaidStep, number> = { 1: 10, 2: 30, 3: 190 };
const PROVINCES: Record<string, string> = {
  roma: 'RM', milano: 'MI', napoli: 'NA', torino: 'TO', bologna: 'BO', firenze: 'FI',
  genova: 'GE', palermo: 'PA', bari: 'BA', venezia: 'VE', verona: 'VR', catania: 'CT',
};

function price(cents: number) {
  return money.format(cents / 100);
}

function addressDefaults(address: string | null) {
  const chunks = address?.split(',').map((part) => part.trim()).filter(Boolean) ?? [];
  const street = chunks[0] ?? '';
  // Google scrive "Via Filogaso, 3, 00173 Roma RM": il comune sta nell'ULTIMO
  // pezzo, ripulito da CAP e sigla. Prendere il secondo pezzo, come si faceva
  // prima, significava spedire al catasto il numero civico al posto del comune.
  const last = chunks.length > 1 ? chunks[chunks.length - 1] : '';
  const comune = last
    .replace(/\b\d{5}\b/g, '')
    .replace(/\b[A-Z]{2}\b\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
  // il civico può stare da solo in un pezzo ("…, 3, …") oppure in coda alla via
  const CIVIC = /^\d+[A-Za-z]?(?:[/-]\w+)?$/;
  const civicChunk = chunks.slice(1, -1).find((part) => CIVIC.test(part));
  const civicMatch = street.match(/(?:^|\s)(\d+[A-Za-z]?(?:[/-]\w+)?)(?:\s*$)/);
  const civico = civicChunk ?? civicMatch?.[1] ?? '';
  const via = civicMatch ? street.slice(0, civicMatch.index).trim() : street;
  const provincia = last.match(/\b([A-Z]{2})\b\s*$/)?.[1]
    ?? PROVINCES[comune.toLocaleLowerCase('it-IT')]
    ?? chunks.join(' ').match(/\b([A-Z]{2})\b/)?.[1] ?? '';
  return { provincia, comune, via, civico };
}

function rowValue(row: JsonRow, keys: string[]) {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number') return `${value}`;
  }
  return '';
}

function rowsFrom(value: unknown, keys: string[]): JsonRow[] {
  if (Array.isArray(value)) return value.filter((item): item is JsonRow => Boolean(item) && typeof item === 'object' && !Array.isArray(item));
  if (!value || typeof value !== 'object') return [];
  const record = value as JsonRow;
  for (const key of [...keys, 'risultato', 'result', 'data']) {
    if (key in record) {
      const rows = rowsFrom(record[key], keys);
      if (rows.length > 0) return rows;
    }
  }
  return [];
}

function addressChoices(result: unknown): CatastoChoice[] {
  return rowsFrom(result, ['indirizzi', 'addresses']).map((row) => ({
    id: rowValue(row, ['id_indirizzo', 'idIndirizzo', 'id']),
    label: rowValue(row, ['indirizzo', 'label', 'denominazione', 'via']) || 'Indirizzo catastale',
    detail: [rowValue(row, ['comune']), rowValue(row, ['provincia'])].filter(Boolean).join(' · ') || undefined,
  })).filter((row) => row.id);
}

function propertyChoices(result: unknown): CatastoChoice[] {
  return rowsFrom(result, ['immobili', 'properties', 'fabbricati']).map((row, index) => {
    const category = rowValue(row, ['categoria', 'categoria_catastale', 'category']);
    const sheet = rowValue(row, ['foglio']);
    const parcel = rowValue(row, ['particella', 'mappale']);
    const sub = rowValue(row, ['subalterno', 'sub']);
    return {
      id: rowValue(row, ['id_immobile', 'idImmobile', 'id']),
      label: category || `Immobile ${index + 1}`,
      detail: [sheet && `Foglio ${sheet}`, parcel && `Particella ${parcel}`, sub && `Sub ${sub}`].filter(Boolean).join(' · ') || undefined,
    };
  }).filter((row) => row.id);
}

function useResolvedStep(step: CatastoStepResult | undefined) {
  const status = useQuery({
    queryKey: ['catasto-request', step?.requestId ?? ''],
    queryFn: () => fetchCatastoRequest(step!.requestId),
    enabled: Boolean(step && !step.ready),
    refetchInterval: (query) => query.state.data?.ready ? false : 6_000,
    refetchIntervalInBackground: false,
  });
  return { value: status.data ?? step, status };
}

function PaidConfirmation({ step, title, detail, acknowledged, pending, onAcknowledge, onCancel, onConfirm }: {
  step: PaidStep;
  title: string;
  detail: string;
  acknowledged: boolean;
  pending: boolean;
  onAcknowledge: (value: boolean) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return <section className="catasto-confirm" aria-labelledby={`catasto-confirm-${step}`}>
    <p className="eyebrow">Conferma il passo {step}</p>
    <h3 id={`catasto-confirm-${step}`}>{title}</h3>
    <p>{detail} Il costo di questo passaggio è <strong>{price(STEP_PRICE[step])}</strong>.</p>
    <label><input type="checkbox" checked={acknowledged} onChange={(event) => onAcknowledge(event.target.checked)}/><span>Ho letto il prezzo e autorizzo questo singolo addebito.</span></label>
    <div><button type="button" className="btn-quiet" disabled={pending} onClick={onCancel}>Annulla</button><button type="button" className="btn-danger" disabled={!acknowledged || pending} onClick={onConfirm}>{pending ? 'Invio la richiesta…' : `Conferma passo ${step} · ${price(STEP_PRICE[step])}`}</button></div>
  </section>;
}

function CatastoHistoryRow({ request }: { request: CatastoHistoryRequest }) {
  const ready = /evasa|completata/i.test(request.status);
  const download = useMutation({ mutationFn: () => downloadCatastoDocument(request.id), onSuccess: saveDownloadedFile });
  return <article className="catasto-history-row"><span className={`catasto-history-state${ready ? ' is-ready' : ''}`} aria-hidden="true">{ready ? '✓' : '…'}</span><div><strong>{request.label}</strong><p>{request.status}</p><small>{dateTime.format(new Date(request.createdAt))} · {price(request.priceCents)}</small></div>{request.kind === 'visura' && ready && <button type="button" disabled={download.isPending} onClick={() => download.mutate()}>{download.isPending ? 'Preparo…' : 'Scarica PDF'}</button>}{download.isError && <p className="catasto-inline-error">{errorMessage(download.error)}</p>}</article>;
}

export default function CatastoProperty({ address, activityName }: { address: string | null; activityName: string }) {
  const defaults = useMemo(() => addressDefaults(address), [address]);
  const [provincia, setProvincia] = useState(defaults.provincia);
  const [comune, setComune] = useState(defaults.comune);
  const [via, setVia] = useState(defaults.via);
  const [civico, setCivico] = useState(defaults.civico);
  const [confirming, setConfirming] = useState<PaidStep | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<CatastoChoice | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<CatastoChoice | null>(null);
  const history = useQuery({ queryKey: ['catasto-history'], queryFn: fetchCatastoHistory });
  const search = useMutation({ mutationFn: searchCatastoAddress, onSuccess: () => { setConfirming(null); setAcknowledged(false); history.refetch(); } });
  const immobili = useMutation({ mutationFn: searchCatastoImmobili, onSuccess: () => { setConfirming(null); setAcknowledged(false); history.refetch(); } });
  const visura = useMutation({ mutationFn: orderCatastoVisura, onSuccess: () => { setConfirming(null); setAcknowledged(false); history.refetch(); } });
  const searchStep = useResolvedStep(search.data);
  const immobiliStep = useResolvedStep(immobili.data);
  const visuraStep = useResolvedStep(visura.data);
  const addresses = addressChoices(searchStep.value?.result);
  const properties = propertyChoices(immobiliStep.value?.result);
  const currentDownload = useMutation({ mutationFn: () => downloadCatastoDocument(visuraStep.value!.requestId), onSuccess: saveDownloadedFile });

  useEffect(() => {
    setProvincia(defaults.provincia);
    setComune(defaults.comune);
    setVia(defaults.via);
    setCivico(defaults.civico);
    setSelectedAddress(null);
    setSelectedProperty(null);
    setConfirming(null);
    setAcknowledged(false);
    search.reset();
    immobili.reset();
    visura.reset();
  }, [address]);

  const openConfirmation = (step: PaidStep) => {
    setConfirming(step);
    setAcknowledged(false);
    if (step === 1) {
      search.reset();
      immobili.reset();
      visura.reset();
      setSelectedAddress(null);
      setSelectedProperty(null);
    } else if (step === 2) {
      immobili.reset();
      visura.reset();
      setSelectedProperty(null);
    } else {
      visura.reset();
    }
  };
  const resetWizard = () => {
    setSelectedAddress(null);
    setSelectedProperty(null);
    setConfirming(null);
    setAcknowledged(false);
    search.reset();
    immobili.reset();
    visura.reset();
  };
  const submitAddress = (event: FormEvent) => {
    event.preventDefault();
    if (provincia.trim() && comune.trim() && via.trim()) openConfirmation(1);
  };
  const error = search.error ?? searchStep.status.error ?? immobili.error ?? immobiliStep.status.error ?? visura.error ?? visuraStep.status.error ?? currentDownload.error;

  return <section className="catasto-property local-detail-section" aria-labelledby="catasto-title">
    <header><div><p className="eyebrow">Catasto · lente acquisizione</p><h2 id="catasto-title">Proprietà dell’immobile</h2></div><span className="catasto-paid">3 passaggi a pagamento</span></header>
    <p className="catasto-intro">Verifica chi risulta intestatario del locale. Ogni richiesta parte soltanto dopo una conferma separata; completare tutti i passaggi costa {price(230)}.</p>

    <ol className="catasto-steps" aria-label="Passaggi e prezzi del catasto">
      <li className={searchStep.value ? 'is-done' : 'is-current'}><span>1</span><div><strong>Trova indirizzo</strong><small>{price(10)}</small></div></li>
      <li className={immobiliStep.value ? 'is-done' : selectedAddress ? 'is-current' : ''}><span>2</span><div><strong>Trova immobili</strong><small>{price(30)}</small></div></li>
      <li className={visuraStep.value ? 'is-done' : selectedProperty ? 'is-current' : ''}><span>3</span><div><strong>Visura intestatari</strong><small>{price(190)}</small></div></li>
    </ol>

    {!searchStep.value && <form className="catasto-search-form" onSubmit={submitAddress}>
      <div className="catasto-field-grid"><label><span>Provincia</span><input value={provincia} maxLength={3} onChange={(event) => setProvincia(event.target.value.toUpperCase())} placeholder="RM" autoCapitalize="characters"/></label><label><span>Comune</span><input value={comune} onChange={(event) => setComune(event.target.value)} placeholder="Roma"/></label></div>
      <label><span>Via, senza civico</span><input value={via} onChange={(event) => setVia(event.target.value)} placeholder="Via del Pigneto"/></label>
      <button type="submit" className="btn-secondary" disabled={!provincia.trim() || !comune.trim() || !via.trim()}>Prepara ricerca · {price(10)}</button>
    </form>}

    {confirming === 1 && <PaidConfirmation step={1} title="Cercare l’indirizzo catastale?" detail={`${via}, ${comune} (${provincia})`} acknowledged={acknowledged} pending={search.isPending} onAcknowledge={setAcknowledged} onCancel={() => setConfirming(null)} onConfirm={() => search.mutate({ provincia: provincia.trim(), comune: comune.trim(), via: via.trim() })}/>}

    {searchStep.value && !searchStep.value.ready && <div className="catasto-pending" role="status"><i/><div><strong>Ricerca indirizzo in elaborazione</strong><p>{searchStep.value.status}. La pagina controlla automaticamente lo stato.</p></div></div>}
    {searchStep.value?.ready && !selectedAddress && <section className="catasto-choice-stage" aria-labelledby="catasto-address-results"><header><h3 id="catasto-address-results">Scegli l’indirizzo corretto</h3><button type="button" className="btn-quiet" onClick={resetWizard}>Nuova ricerca</button></header>{addresses.length > 0 ? <div className="catasto-choice-list">{addresses.map((item) => <button type="button" key={item.id} onClick={() => { setSelectedAddress(item); setSelectedProperty(null); immobili.reset(); visura.reset(); }}><span aria-hidden="true">○</span><span><strong>{item.label}</strong>{item.detail && <small>{item.detail}</small>}</span><b aria-hidden="true">→</b></button>)}</div> : <p className="catasto-empty">La richiesta è pronta, ma non contiene indirizzi selezionabili. Prova una via più precisa.</p>}</section>}

    {selectedAddress && !immobiliStep.value && <section className="catasto-civic-stage"><div><span>Indirizzo selezionato</span><strong>{selectedAddress.label}</strong></div><label><span>Civico</span><input value={civico} onChange={(event) => setCivico(event.target.value)} placeholder="73"/></label><button type="button" className="btn-secondary" disabled={!civico.trim()} onClick={() => openConfirmation(2)}>Prepara immobili · {price(30)}</button></section>}
    {confirming === 2 && selectedAddress && <PaidConfirmation step={2} title="Cercare gli immobili al civico?" detail={`${selectedAddress.label}, civico ${civico}`} acknowledged={acknowledged} pending={immobili.isPending} onAcknowledge={setAcknowledged} onCancel={() => setConfirming(null)} onConfirm={() => immobili.mutate({ idIndirizzo: selectedAddress.id, civico: civico.trim(), label: `${selectedAddress.label} ${civico}` })}/>}
    {immobiliStep.value && !immobiliStep.value.ready && <div className="catasto-pending" role="status"><i/><div><strong>Immobili in elaborazione</strong><p>{immobiliStep.value.status}. Aggiorniamo automaticamente questa fase.</p></div></div>}
    {immobiliStep.value?.ready && !selectedProperty && <section className="catasto-choice-stage" aria-labelledby="catasto-property-results"><header><h3 id="catasto-property-results">Scegli l’unità immobiliare</h3></header>{properties.length > 0 ? <div className="catasto-choice-list">{properties.map((item) => <button type="button" key={item.id} onClick={() => { setSelectedProperty(item); visura.reset(); }}><span aria-hidden="true">○</span><span><strong>{item.label}</strong>{item.detail && <small>{item.detail}</small>}</span><b aria-hidden="true">→</b></button>)}</div> : <p className="catasto-empty">Nessun immobile selezionabile nel risultato ricevuto.</p>}</section>}

    {selectedProperty && !visuraStep.value && <section className="catasto-visura-stage"><div><span>Immobile selezionato</span><strong>{selectedProperty.label}</strong>{selectedProperty.detail && <small>{selectedProperty.detail}</small>}</div><button type="button" className="btn-secondary" onClick={() => openConfirmation(3)}>Prepara visura intestatari · {price(190)}</button></section>}
    {confirming === 3 && selectedProperty && <PaidConfirmation step={3} title="Richiedere la visura con intestatari?" detail={`Documento PDF ufficiale per ${activityName} · ${selectedProperty.label}.`} acknowledged={acknowledged} pending={visura.isPending} onAcknowledge={setAcknowledged} onCancel={() => setConfirming(null)} onConfirm={() => visura.mutate({ idImmobile: selectedProperty.id, label: `${activityName} · ${selectedProperty.label}` })}/>}
    {visuraStep.value && !visuraStep.value.ready && <div className="catasto-pending" role="status"><i/><div><strong>Visura in elaborazione</strong><p>{visuraStep.value.status}. Il PDF sarà disponibile appena pronto.</p></div></div>}
    {visuraStep.value?.ready && <div className="catasto-ready" role="status"><span aria-hidden="true">✓</span><div><strong>Visura pronta</strong><p>Il PDF ufficiale contiene gli intestatari risultanti al Catasto.</p></div><button type="button" disabled={currentDownload.isPending} onClick={() => currentDownload.mutate()}>{currentDownload.isPending ? 'Preparo…' : 'Scarica PDF'}</button></div>}

    {error && <div className="notice notice-error"><p>{errorMessage(error)}</p></div>}
    {history.isPending && <div className="catasto-history-loading" role="status">Carico lo storico catastale…</div>}
    {history.isError && <div className="notice notice-error"><p>{errorMessage(history.error)}</p><button type="button" className="btn-secondary" onClick={() => history.refetch()}>Riprova</button></div>}
    {history.data && history.data.length > 0 && <section className="catasto-history" aria-labelledby="catasto-history-title"><header><div><p className="eyebrow">Richieste precedenti</p><h3 id="catasto-history-title">Storico catasto</h3></div><span>{history.data.length}</span></header><div>{history.data.slice(0, 6).map((request) => <CatastoHistoryRow key={request.id} request={request}/>)}</div></section>}
  </section>;
}
